import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import {
  EmpleadoModalidad,
  Ubicacion,
} from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../../asistencia.service";
import {
  DiaPatron,
  RenglonConPatron,
  aplanarPatron,
  patronDesdeServidor,
} from "./patron-semanal";

/** Lo que la lista le pasa al modal. */
export interface DatosModalModalidad {
  /** Si es MIXTO, trae el patrón vigente en `dias` (ver patron-semanal.ts). */
  renglon: RenglonConPatron;
  ubicaciones: Ubicacion[];
}

/**
 * Asigna la modalidad de una persona, con vigencia.
 *
 * TRES COSAS QUE ESTA PANTALLA TIENE QUE DEJAR CLARAS
 *
 * 1. Asignar NO pisa lo anterior: cierra la vigencia que corría el día
 *    antes de la nueva. Es lo que permite que un reporte de hace tres
 *    meses se siga explicando con lo que regía entonces.
 * 2. Quien es presencial NECESITA sitio. Sin él la evaluación no sabe
 *    dónde se le esperaba, y el servidor lo rechaza; se pide aquí para
 *    que no se descubra al guardar.
 * 3. MIXTO no es un lugar: significa que el lugar lo decide el patrón
 *    semanal o el plan del día. Por eso el patrón sólo se ofrece ahí, con
 *    mañana y tarde por día como en el kiosco, y arranca con el que ya
 *    tiene la persona, no con una semana nueva.
 */
@Component({
  selector: "app-modal-modalidad",
  standalone: true,
  templateUrl: "./modal-modalidad.component.html",
  styleUrls: ["./modal-modalidad.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class ModalModalidadComponent implements OnInit {
  modalidad = new EmpleadoModalidad();
  guardando = false;

  /** Las que de verdad se usan; la lista completa sería ruido. */
  readonly zonas = [
    "America/Mexico_City",
    "America/Cancun",
    "America/Chihuahua",
    "America/Hermosillo",
    "America/Tijuana",
    "America/Mazatlan",
    "America/Merida",
    "America/Monterrey",
  ];

  /**
   * Lunes a domingo, cada uno con su mañana y su tarde; se aplana al
   * guardar. Es un arreglo fijo: los desplegables se ligan a sus renglones
   * y nunca se recrean.
   */
  patron: DiaPatron[] = patronDesdeServidor(null);

  constructor(
    public modalDialog: MatDialogRef<ModalModalidadComponent>,
    @Inject(MAT_DIALOG_DATA) public datos: DatosModalModalidad,
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.modalidad.idEmpleado = this.datos.renglon.idEmpleado;
    this.modalidad.modalidad = this.datos.renglon.modalidad ?? "OFICINA";
    this.modalidad.idUbicacionBase = this.datos.renglon.idUbicacionBase ?? null;
    this.modalidad.zonaHoraria = this.datos.renglon.zonaHoraria ?? null;
    // Por omisión rige desde hoy: lo normal es "a partir de ya", y una
    // fecha vacía obligaría a teclearla en el caso más común.
    this.modalidad.fechaInicioVigencia = this.hoy();
    // El patrón vigente viene en el renglón (sólo para MIXTO). Arrancar con
    // la semana en oficina hacía que "Cambiar modalidad" + Guardar pisara
    // el patrón de la persona aunque sólo se quisiera mover la vigencia.
    this.patron = patronDesdeServidor(this.datos.renglon.dias);
  }

  porDia(_indice: number, dia: DiaPatron): number {
    return dia.diaSemana;
  }

  get persona(): string {
    return this.datos.renglon.nombreEmpleado;
  }

  get esMixto(): boolean {
    return this.modalidad.modalidad === "MIXTO";
  }

  /** Home office puro es el único que no necesita sitio. */
  get necesitaSitio(): boolean {
    return this.modalidad.modalidad !== "HOME_OFFICE";
  }

  get titulo(): string {
    return this.datos.renglon.modalidad ? "Cambiar modalidad" : "Asignar modalidad";
  }

  /** Los sitios donde de verdad se puede esperar a alguien. */
  get sitiosElegibles(): Ubicacion[] {
    return this.datos.ubicaciones.filter((sitio) => sitio.tipo !== "HOME_OFFICE");
  }

  alCambiarModalidad(): void {
    if (!this.necesitaSitio) {
      this.modalidad.idUbicacionBase = null;
    }
  }

  guardar(): void {
    const problema = this.primerProblema();
    if (problema) {
      this.msg.warningCall(problema, null, null);
      return;
    }

    this.modalidad.dias = this.esMixto ? aplanarPatron(this.patron) : [];

    this.guardando = true;
    this.cdr.detectChanges();

    this.asistenciaService.asignarModalidad(this.modalidad).subscribe({
      next: (respuesta) => {
        this.guardando = false;
        // El negocio devuelve la modalidad guardada; si algo la rechazó,
        // devuelve el motivo y sin id.
        if (!respuesta?.idModalidad) {
          this.msg.warningCall(
            respuesta?.mensaje || "No se pudo asignar la modalidad.",
            null,
            null
          );
          this.cdr.detectChanges();
          return;
        }
        this.msg.successCall("La modalidad quedó asignada.", null, null);
        this.modalDialog.close(respuesta);
      },
      error: () => {
        this.guardando = false;
        this.msg.errorCall("No se pudo asignar la modalidad.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  cancelar(): void {
    this.modalDialog.close(null);
  }

  // ------------------------------------------------------------------

  /**
   * Se valida aquí lo mismo que valida el servidor, no para confiar en
   * ello sino para no hacer viajar a nadie hasta el rechazo.
   */
  private primerProblema(): string {
    if (!this.modalidad.modalidad) {
      return "Elija si trabaja en sitio, desde casa o mixto.";
    }
    if (this.necesitaSitio && !this.modalidad.idUbicacionBase) {
      return "Indique en qué sitio se le espera: sin él la evaluación no sabe dónde debía estar.";
    }
    if (!this.modalidad.fechaInicioVigencia) {
      return "Indique desde qué día rige.";
    }
    if (
      this.modalidad.fechaFinVigencia &&
      `${this.modalidad.fechaFinVigencia}` < `${this.modalidad.fechaInicioVigencia}`
    ) {
      return "La vigencia no puede terminar antes de empezar.";
    }
    return null;
  }

  private hoy(): string {
    const ahora = new Date();
    const mes = `${ahora.getMonth() + 1}`.padStart(2, "0");
    const dia = `${ahora.getDate()}`.padStart(2, "0");
    return `${ahora.getFullYear()}-${mes}-${dia}`;
  }
}
